import { useEffect, useState } from "react";
import StatCard from "pages/dashboard-overview/components/StatCard";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from "pages/analytics/components/analyticsFormatters";
import { getMrr } from "services/analytics-truth.service";
import { getSubscriptionMetrics } from "services/finance.service";

const StatCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
    <div className="flex justify-between items-start">
      <div className="flex-1">
        <div className="h-4 w-28 bg-gray-200 animate-pulse rounded" />
        <div className="h-8 w-24 bg-gray-200 animate-pulse rounded mt-2" />
      </div>
      <div className="h-9 w-9 bg-gray-200 animate-pulse rounded-lg" />
    </div>
  </div>
);

const isCustomRangeIncomplete = (filters) =>
  filters?.dateRangeType === "custom" &&
  (!filters?.customDateRange?.[0] || !filters?.customDateRange?.[1]);

export default function SubscriptionKpiCards({ api, filters }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [mrrResult, subscriptionMetrics] = await Promise.all([
          getMrr(api, { filters }),
          getSubscriptionMetrics(api, { filters }),
        ]);
        if (!cancelled) {
          setData({
            activeSubscribers: mrrResult?.activeSubscribers ?? 0,
            mrr: mrrResult?.value ?? 0,
            churnPercent: subscriptionMetrics?.churnPercent ?? null,
          });
        }
      } catch {
        if (!cancelled) {
          setData(null);
          setError("Failed to load subscription metrics. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  const showEmpty =
    !isLoading && !error && !isCustomRangeIncomplete(filters) && !data;

  const cards = [
    {
      title: "Active Subscribers",
      value: formatNumber(data?.activeSubscribers),
      icon: "Users",
    },
    {
      title: "MRR",
      value: formatCurrency(data?.mrr),
      icon: "DollarSign",
    },
    {
      title: "Churn %",
      value: formatPercent(data?.churnPercent),
      icon: "UserMinus",
    },
  ];

  return (
    <div className="mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-3">Subscriptions</h2>

      {error && !isLoading && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {isCustomRangeIncomplete(filters) && !isLoading && (
        <p className="text-sm text-gray-500 italic">
          Select start and end dates to view subscription metrics.
        </p>
      )}

      {showEmpty && (
        <p className="text-sm text-gray-500 italic">
          No subscription metrics available for the selected period.
        </p>
      )}

      {(isLoading || data) && !isCustomRangeIncomplete(filters) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading
            ? [...Array(3)].map((_, i) => <StatCardSkeleton key={i} />)
            : cards.map((card) => (
                <StatCard
                  key={card.title}
                  title={card.title}
                  value={card.value}
                  icon={card.icon}
                />
              ))}
        </div>
      )}
    </div>
  );
}
